<script setup lang="ts">
import { onUnmounted, ref, shallowRef, watch } from "vue";
import { useMap } from "../composable/google-map";
import { useMarkerOverlay, type MarkerOverlay } from "../composable/marker-overlay";

type ListenerCleanup = (() => void) | google.maps.MapsEventListener;

const props = defineProps<{
	position: google.maps.LatLng | google.maps.LatLngLiteral;
}>();

const overlay = shallowRef<MarkerOverlay>();
const listeners: ListenerCleanup[] = [];

const content = shallowRef<HTMLDivElement>();

let contentWatch: MutationObserver | null = null;

function clearOverlay() {
	if (overlay.value) {
		overlay.value.setMap(null);
	}

	for (const listener of listeners) {
		if ("remove" in listener) {
			listener.remove();
		} else {
			listener();
		}
	}
	listeners.length = 0;

	if (contentWatch != null) {
		contentWatch.disconnect();
		contentWatch = null;
	}
}

const map = useMap();

// const initialised = computed(() => map.value != null);

watch(
	() => props.position,
	() => {
		overlay.value?.setPosition(props.position);
	}
);

let initialised = ref(false);
watch(
	[map, content],
	() => {
		if (!map.value || !content.value) return;

		clearOverlay();

		const MarkerOverlay = useMarkerOverlay();

		overlay.value = new MarkerOverlay({
			map: map.value,
			position: props.position,
			content: content.value,
		});

		contentWatch = new MutationObserver(() => {
			if (overlay.value) {
				overlay.value.draw();
			}
		});
		contentWatch.observe(content.value, {
			attributes: true,
			childList: true,
			subtree: true,
		});

		// Wait for next render, to prevent quick tooltip flashing at the 0,0 position.
		requestAnimationFrame(() => {
			initialised.value = true;
		});
	},
	{ immediate: true }
);

onUnmounted(clearOverlay);

const mouseIn = ref(false);
</script>

<template>
	<div
		ref="content"
		class="position-absolute"
		:style="{
			zIndex: mouseIn ? 999 : undefined,
		}"
		@mouseenter.stop="mouseIn = true"
		@mouseleave.stop="mouseIn = false"
	>
		<slot v-if="initialised" />
	</div>
</template>

<style>
.position-absolute {
	position: absolute;
}
.popup-bubble {
	position: absolute;
	top: 0;
	left: 0;
}

.popup-project {
	cursor: auto;
	height: 0;
	position: absolute;
	width: 200px;
}

.popup-bubble-anchor {
	position: absolute;
	width: 100%;
	left: 0;
}

.popup-bubble-anchor::after {
	content: "";
	position: absolute;
	top: 0;
	left: 0;
}

.gm-style {
	font: inherit;
}
</style>
